import { useLanguage } from '../contexts/LanguageContext';
import { HomeFeatureSection } from './home/HomeFeatureSection';
import { HomeMarqueeSection } from './home/HomeMarqueeSection';
import { HomeFAQSection } from './home/HomeFAQSection';
import { HomeCoreFeaturesStack } from './home/HomeCoreFeaturesStack';

interface LandingPageProps {
  onToggleLang: () => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEnterApp: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onToggleLang, onEnterApp }) => {
  const { lang, t } = useLanguage();
  return (
    <div
      className="w-full min-h-screen overflow-x-hidden overflow-y-auto relative"
    >
      {/* Existing Hero Section Wrapper */}
      <div
        className="h-screen w-full py-4 relative cursor-pointer"
        onClick={onEnterApp}
      >
        {/* 图片容器 */}
        <div className="w-full h-full rounded-[2rem] overflow-hidden shadow-2xl shadow-black/60 border border-white/30 relative" style={{ transform: 'translateZ(0)' }}>
          <img
            src="/pen-hero.png"
            alt="DocSure AI"
            className="w-full h-full object-cover"
          />

          {/* 左下角语言切换 - 地球图标 */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleLang(); }}
            className="absolute bottom-4 left-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer pointer-events-auto transition-opacity hover:opacity-100 opacity-80"
            style={{
              background: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
            title={t('hero.switch')}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          </button>

          {/* 主反光条 - 更厚 */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              willChange: 'opacity, transform',
              transform: 'translateZ(0)',
              background: `
              linear-gradient(
                115deg,
                transparent 0%,
                transparent 20%,
                rgba(255, 255, 255, 0.15) 25%,
                rgba(255, 255, 255, 0.35) 35%,
                rgba(255, 255, 255, 0.4) 45%,
                rgba(255, 255, 255, 0.35) 55%,
                rgba(255, 255, 255, 0.15) 65%,
                transparent 70%,
                transparent 100%
              )
            `
            }}
          />

          {/* 顶部强反光 - 更厚 */}
          <div
            className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.25) 30%, rgba(255,255,255,0.1) 60%, transparent 100%)',
              borderRadius: '2rem 2rem 0 0'
            }}
          />

          {/* 左上角高光圆点 - 更大 */}
          <div
            className="absolute top-0 left-0 w-72 h-72 pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.3) 30%, rgba(255,255,255,0.1) 60%, transparent 80%)',
              filter: 'blur(16px)'
            }}
          />

          {/* 第二个高光点 - 更大 */}
          <div
            className="absolute top-8 left-20 w-40 h-40 pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, transparent 70%)',
              filter: 'blur(10px)'
            }}
          />

          {/* 玻璃边缘高光 - 顶部更厚 */}
          <div
            className="absolute top-0 left-8 right-8 h-[4px] pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9) 15%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.9) 85%, transparent)',
              filter: 'blur(1px)'
            }}
          />

          {/* 玻璃边缘高光 - 左侧更厚 */}
          <div
            className="absolute top-8 bottom-8 left-0 w-[4px] pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.8) 15%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0.8) 85%, transparent)',
              filter: 'blur(1px)'
            }}
          />

          {/* 内发光边框 - 更强 */}
          <div
            className="absolute inset-0 rounded-[2rem] pointer-events-none"
            style={{
              boxShadow: `
              inset 0 4px 8px rgba(255,255,255,0.4),
              inset 4px 0 8px rgba(255,255,255,0.25),
              inset 0 -2px 4px rgba(0,0,0,0.4),
              inset -2px 0 4px rgba(0,0,0,0.3)
            `
            }}
          />

          {/* 整体提亮 */}
          <div
            className="absolute inset-0 pointer-events-none rounded-[2rem]"
            style={{
              background: 'rgba(255,255,255,0.04)'
            }}
          />
        </div>

        {/* 左上角 Logo + 名字 - 凹陷效果 */}
        <div className="absolute top-[8vh] left-[6vw] pointer-events-none flex items-center gap-[0.8vw]">
          <img
            src="/docsure logo.png"
            alt="DocSure Logo"
            className="h-[3.5vh] w-auto rounded-md"
            style={{
              filter: 'drop-shadow(1px 1px 0 rgba(255,255,255,0.3)) drop-shadow(-0.5px -0.5px 0 rgba(0,0,0,0.2))',
              opacity: 0.9
            }}
          />
          <span
            className="font-semibold"
            style={{
              fontSize: '1.5vw',
              color: '#1a1a2e',
              textShadow: '1px 1px 0 rgba(255,255,255,0.4), -0.5px -0.5px 0 rgba(0,0,0,0.15)',
              opacity: 0.9
            }}
          >DocSure AI</span>
        </div>

        {/* 标题文字 - 凹陷效果 */}
        <div className="absolute top-[26vh] left-[6vw] pointer-events-none">
          <h1
            className="font-bold leading-tight mb-[4vh]"
            style={{
              fontSize: '4.5vw',
              fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
              color: 'transparent',
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              textShadow: `
              1px 1px 1px rgba(255,255,255,0.4),
              2px 2px 3px rgba(255,255,255,0.2),
              -1px -1px 1px rgba(0,0,0,0.3)
            `,
              filter: 'drop-shadow(0 1px 0 rgba(255,255,255,0.3))'
            }}
          >
            {t('hero.title1')}<br />
            {t('hero.title2')}
          </h1>
          <p
            className="leading-relaxed mb-[5vh]"
            style={{
              fontSize: '1.6vw',
              fontFamily: "'Inter', sans-serif",
              color: '#2a2a3a',
              textShadow: `
              1px 1px 1px rgba(255,255,255,0.5),
              -0.5px -0.5px 0.5px rgba(0,0,0,0.15)
            `,
              whiteSpace: 'pre-line'
            }}
          >
            {t('hero.subtitle')}
          </p>
        </div>

        {/* Get Free Plugin 按钮 - 黑灰色包浆质感 */}
        <button
          onClick={(e) => { e.stopPropagation(); onEnterApp(); }}
          className="absolute top-[72vh] left-[6vw] inline-flex items-center gap-[0.8vw] px-[3vw] py-[1.2vh] rounded-full cursor-pointer group overflow-hidden transition-transform duration-300 hover:scale-105 w-fit"
          style={{
            background: 'linear-gradient(180deg, #555555 0%, #404040 20%, #333333 50%, #2a2a2a 100%)',
            boxShadow: `
            0 6px 20px rgba(0,0,0,0.5),
            0 2px 6px rgba(0,0,0,0.4),
            inset 0 2px 4px rgba(255,255,255,0.15),
            inset 0 -2px 4px rgba(0,0,0,0.3)
          `,
          }}
        >
          {/* 包浆油润光泽 - 左上 */}
          <div
            className="absolute top-0 left-0 w-1/2 h-full rounded-l-full pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 100% 80% at 20% 30%, rgba(255,255,255,0.2) 0%, transparent 50%)'
            }}
          />

          {/* 包浆油润光泽 - 右侧 */}
          <div
            className="absolute top-0 right-0 w-1/3 h-full rounded-r-full pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 80% 60% at 80% 60%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            }}
          />

          {/* 顶部强褪色 */}
          <div
            className="absolute top-0 left-0 right-0 h-2/3 rounded-t-full pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 40%, transparent 100%)'
            }}
          />

          {/* 顶部高光线 */}
          <div
            className="absolute top-0 left-6 right-6 h-[2px] rounded-full pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4) 20%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.4) 80%, transparent)'
            }}
          />

          {/* 底部暗角 */}
          <div
            className="absolute bottom-0 left-0 right-0 h-1/3 rounded-b-full pointer-events-none"
            style={{
              background: 'linear-gradient(0deg, rgba(0,0,0,0.2) 0%, transparent 100%)'
            }}
          />

          <span
            className="text-white/90 relative z-10 italic tracking-wide"
            style={{
              fontSize: '1.2vw',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              fontStyle: 'italic',
              letterSpacing: '0.05em',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}
          > {t('hero.getStarted')}</span>
        </button>




      </div>

      {/* New Sections */}
      <HomeFeatureSection />
      <HomeMarqueeSection />
      <HomeCoreFeaturesStack />

      {/* Try Now Button & Footer Image Container */}
      <div className="w-full relative -mt-32 flex flex-col items-center">

        {/* Try Now Button */}
        <button
          onClick={(e) => { e.stopPropagation(); onEnterApp(); }}
          className="relative z-30 mb-20 px-12 py-4 rounded-full font-bold text-lg text-black transition-all duration-300 hover:scale-105 hover:shadow-white/25 group"
          style={{
            background: '#ffffff',
            boxShadow: '0 10px 30px -10px rgba(255, 255, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 1)'
          }}
        >
          <span className="relative z-10 flex items-center gap-2">
            {t('action.tryNow')}
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </span>
        </button>

        <div className="w-full relative">
          {/* Gradient Mask to blend top edge 'arc' into background */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#1a1a1a] to-transparent z-10"></div>

          <img
            src="/落地页.png"
            alt="DocSure Footer"
            className="w-full h-auto block"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%)'
            }}
          />

          {/* Footer Overlay */}
          <footer className="absolute bottom-8 left-0 right-0 text-gray-400 py-8 text-center z-20">
          </footer>
        </div>
      </div>
    </div>
  );
};
